import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import { Timestamp } from "firebase/firestore";
import { useAuth } from "../auth/AuthProvider";
import Goal from "../models/Goal";
import "react-datepicker/dist/react-datepicker.css";

const UNSPLASH_ACCESS_KEY = process.env.REACT_APP_UNSPLASH_ACCESS_KEY;

const AddGoalPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("strength");
  const [targetValue, setTargetValue] = useState("");
  const [targetUnit, setTargetUnit] = useState("kg");
  const [targetReps, setTargetReps] = useState("");
  const [direction, setDirection] = useState("increase");
  const [deadline, setDeadline] = useState(
    new Date(new Date().getFullYear(), 11, 31) // Dec 31 of current year
  );
  const [startingValue, setStartingValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Image search state
  const [imageSearch, setImageSearch] = useState("");
  const [imageResults, setImageResults] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [searchingImages, setSearchingImages] = useState(false);

  const categories = [
    { value: "strength", label: "Strength" },
    { value: "endurance", label: "Endurance" },
    { value: "skill", label: "Skill" },
    { value: "other", label: "Other" },
  ];

  const units = [
    { value: "kg", label: "kg" },
    { value: "lbs", label: "lbs" },
    { value: "reps", label: "reps" },
    { value: "minutes", label: "minutes" },
    { value: "seconds", label: "seconds" },
    { value: "km", label: "km" },
    { value: "miles", label: "miles" },
  ];

  const searchImages = async () => {
    if (!imageSearch.trim()) return;

    setSearchingImages(true);
    try {
      const response = await fetch(
        `https://api.unsplash.com/search/photos?query=${encodeURIComponent(imageSearch)}&per_page=12&orientation=landscape`,
        {
          headers: {
            Authorization: `Client-ID ${UNSPLASH_ACCESS_KEY}`,
          },
        }
      );
      const data = await response.json();
      setImageResults(data.results || []);
    } catch (err) {
      console.error("Error searching images:", err);
    } finally {
      setSearchingImages(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!title.trim()) {
      setError("Goal title is required");
      return;
    }

    if (!targetValue) {
      setError("Target value is required");
      return;
    }

    setLoading(true);

    try {
      await Goal.create(user.uid, {
        title,
        description,
        category,
        targetValue: parseFloat(targetValue),
        targetUnit,
        targetReps: targetReps ? parseInt(targetReps, 10) : null,
        direction,
        deadline: Timestamp.fromDate(deadline),
        startingValue: startingValue ? parseFloat(startingValue) : null,
        img: selectedImage?.urls?.regular || null,
        imgThumb: selectedImage?.urls?.thumb || null,
        imgCredit: selectedImage ? {
          name: selectedImage.user?.name,
          link: selectedImage.user?.links?.html,
        } : null,
      });

      navigate("/goals");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <button
          onClick={() => navigate("/goals")}
          className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4"
        >
          <svg className="w-5 h-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Create Goal
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Set a target with a deadline and track your progress
        </p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6">
        {error && (
          <div className="mb-4 p-3 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 rounded-lg">
            {error}
          </div>
        )}

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Goal Title *
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g., Bench press 100kg x2"
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            required
          />
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe your goal..."
            rows={2}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />
        </div>

        {/* Image Search */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Cover Image
          </label>

          {selectedImage ? (
            <div className="relative mb-3">
              <img
                src={selectedImage.urls.small}
                alt={selectedImage.alt_description}
                className="w-full h-40 object-cover rounded-lg"
              />
              <button
                type="button"
                onClick={() => setSelectedImage(null)}
                className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Photo by <a href={selectedImage.user?.links?.html} target="_blank" rel="noopener noreferrer" className="underline">{selectedImage.user?.name}</a> on Unsplash
              </p>
            </div>
          ) : (
            <>
              <div className="flex gap-2 mb-3">
                <input
                  type="text"
                  value={imageSearch}
                  onChange={(e) => setImageSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), searchImages())}
                  placeholder="Search for an image..."
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={searchImages}
                  disabled={searchingImages}
                  className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                  {searchingImages ? (
                    <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  )}
                </button>
              </div>

              {imageResults.length > 0 && (
                <div className="grid grid-cols-3 gap-2 max-h-60 overflow-y-auto">
                  {imageResults.map((image) => (
                    <button
                      key={image.id}
                      type="button"
                      onClick={() => setSelectedImage(image)}
                      className="relative aspect-video overflow-hidden rounded-lg hover:ring-2 hover:ring-indigo-500 transition-all"
                    >
                      <img
                        src={image.urls.thumb}
                        alt={image.alt_description}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Category
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              {categories.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Direction
            </label>
            <select
              value={direction}
              onChange={(e) => setDirection(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              <option value="increase">Higher is better (lift more, run further)</option>
              <option value="decrease">Lower is better (run faster, less time)</option>
            </select>
          </div>
        </div>

        {/* Target Value */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Target Value *
          </label>
          <div className="flex gap-2">
            <input
              type="number"
              value={targetValue}
              onChange={(e) => setTargetValue(e.target.value)}
              placeholder="100"
              step="0.1"
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              required
            />
            <select
              value={targetUnit}
              onChange={(e) => setTargetUnit(e.target.value)}
              className="w-28 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              {units.map((unit) => (
                <option key={unit.value} value={unit.value}>
                  {unit.label}
                </option>
              ))}
            </select>
            {category === "strength" && (
              <>
                <span className="flex items-center text-gray-500 dark:text-gray-400">x</span>
                <input
                  type="number"
                  value={targetReps}
                  onChange={(e) => setTargetReps(e.target.value)}
                  placeholder="2"
                  className="w-20 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                <span className="flex items-center text-gray-500 dark:text-gray-400 text-sm">reps</span>
              </>
            )}
          </div>
        </div>

        {/* Deadline */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Deadline
          </label>
          <DatePicker
            selected={deadline}
            onChange={(date) => setDeadline(date)}
            dateFormat="MMMM d, yyyy"
            minDate={new Date()}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            wrapperClassName="w-full"
          />
        </div>

        {/* Starting Value */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Current Baseline (optional)
          </label>
          <div className="flex gap-2 items-center">
            <input
              type="number"
              value={startingValue}
              onChange={(e) => setStartingValue(e.target.value)}
              placeholder="80"
              step="0.1"
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
            <span className="text-gray-500 dark:text-gray-400">{targetUnit}</span>
          </div>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Where are you starting from? This helps track your progress.
          </p>
        </div>

        <div className="flex gap-4">
          <button
            type="button"
            onClick={() => navigate("/goals")}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white rounded-lg font-medium transition-colors"
          >
            {loading ? "Creating..." : "Create Goal"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddGoalPage;
